import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  RadioGroup,
  Radio,
  FormControlLabel,
  InputAdornment, // NEW IMPORT
  IconButton,     // NEW IMPORT
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Visibility from '@mui/icons-material/Visibility';     // NEW IMPORT
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // NEW IMPORT

import api from "core/interceptors/api";
import MDButton from "components/MDButton";
import backgroundImage from "../../../assets/images/backgroundsjust.png";
import { validatePassword } from "layouts/tables/validations";

// (Optional) Custom CSS for ToastContainer if you use the className approach
/*
.full-width-toast-container .Toastify__toast-container {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}

.full-width-toast-container .Toastify__toast {
  width: auto;
  max-width: 100%;
  box-sizing: border-box;
}
*/
// If you create the CSS file, uncomment the import below:
// import '../../../assets/css/custom-toast.css'; // Adjust path if needed

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [option, setOption] = useState("mobile");
  const [identifier, setIdentifier] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(180);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  // NEW STATES for password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handlers for password visibility
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleMouseDownNewPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
  const handleMouseDownConfirmPassword = (event) => {
    event.preventDefault();
  };

  // OTP Countdown
  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (step === 2 && timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Validation functions
  const validateIdentifier = (value) => {
    if (option === "mobile") {
      const valid = /^\d{10}$/.test(value);
      setIdentifierError(valid ? "" : "Enter a valid 10-digit mobile number.");
    } else {
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      setIdentifierError(valid ? "" : "Enter a valid email address.");
    }
    setIdentifier(value);
  };

  const validateOtp = (value) => {
    const valid = /^\d{6}$/.test(value);
    setOtpError(valid || value === "" ? "" : "Enter a valid 6-digit OTP.");
    setOtp(value);
  };

  const validatePasswords = (np, cp) => {
    const passwordValidationError = validatePassword(np);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
    } else if (np !== cp) {
      setPasswordError("Passwords do not match.");
    } else {
      setPasswordError("");
    }
  };

  const handleGetOtp = async () => {
    if (!role) {
      toast.info("Please select a role.");
      return;
    }
    if (!identifier || identifierError) {
      toast.info("Please enter a valid email or mobile number.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/otp_email/login_check", {
        identifier,
        role,
        otp_type: option,
      });

      if (response.data.status === 1) {
        setOtp("");
        setTimer(180);
        setCanResend(false);
        setStep(2);
        toast.success("OTP sent successfully");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to send OTP. Please try again.");
    }
    setLoading(false);
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await api.post("/otp_email/login_check", {
        identifier,
        role,
        otp_type: option,
      });

      if (response.data.status === 1) {
        setOtp("");
        setTimer(180);
        setCanResend(false);
        toast.success("OTP resent successfully.");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to resend OTP. Try again later.");
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otp || otpError) {
      toast.info("Enter a valid OTP.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/otp_email/validate_otp", {
        email_id: identifier,
        otp,
      });
      if (response.data.status === 1) {
        setNewPassword("");
        setConfirmPassword("");
        setStep(3);
        toast.success("OTP verified. You can reset your password.");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Invalid OTP. Please try again.");
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword || passwordError) {
      toast.info("Please enter and confirm your new password.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/vendor_register/confirm_password", {
        data: identifier,
        new_password: newPassword,
        role,
        otp_type: option,
      });

      if (response.data.status === 1) {
        toast.success("Password reset successfully.");
        navigate("/login");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Try again.");
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (step === 1) navigate("/login");
    else setStep(step - 1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box sx={{ background: "#fff", p: 4, borderRadius: 2, boxShadow: 3, width: 400, textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
          Forgot Password
        </Typography>

        {step === 1 && (
          <>
            <Typography align="left" variant="h6" sx={{ mb: 1 }}>
              Select Role:
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={role}
              onChange={(e) => setRole(e.target.value)}
              sx={{ mb: 2, height: 50 }}
              IconComponent={ArrowDropDownIcon}
            >
              <MenuItem value="" disabled>Select</MenuItem>
              <MenuItem value="Vendor">Vendor</MenuItem>
              <MenuItem value="Services">Services</MenuItem>
            </Select>

            {role && (
              <>
                <Stack spacing={2}>
                  <Typography align="left" fontWeight="bold" variant="h6">Choose Verification Method:</Typography>
                  <RadioGroup row value={option} onChange={(e) => {
                    setOption(e.target.value);
                    setIdentifier("");
                    setIdentifierError("");
                  }}>
                    <FormControlLabel value="mobile" control={<Radio color="primary" />} label="Mobile Number" />
                    <FormControlLabel value="email" control={<Radio color="primary" />} label="Email" />
                  </RadioGroup>

                  <TextField
                    fullWidth
                    type={option === "email" ? "email" : "tel"}
                    value={identifier}
                    onChange={(e) => validateIdentifier(e.target.value)}
                    placeholder={option === "email" ? "Enter your email" : "Enter your mobile number"}
                    error={Boolean(identifierError)}
                    helperText={identifierError}
                  />
                </Stack>

                <MDButton
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={handleGetOtp}
                  disabled={loading || Boolean(identifierError) || !identifier}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Next"}
                </MDButton>
              </>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Typography align="left" fontWeight="bold" sx={{ mb: 1 }}>
              Enter OTP:
            </Typography>

            <TextField
              fullWidth
              value={otp}
              onChange={(e) => validateOtp(e.target.value)}
              placeholder="Enter the 6-digit OTP"
              sx={{ mb: 1 }}
              inputProps={{ maxLength: 6 }}
              error={Boolean(otpError)}
              helperText={otpError}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              OTP expires in: {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, "0")}
            </Typography>

            <MDButton
              variant="contained"
              color="info"
              fullWidth
              onClick={handleVerifyOtp}
              disabled={loading || Boolean(otpError) || otp.length !== 6}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
            </MDButton>

            <Button
              disabled={!canResend}
              onClick={handleResendOtp}
              sx={{ mt: 2, textTransform: "none", color: canResend ? "primary.main" : "grey.500" }}
            >
              {canResend ? "Resend OTP" : "Resend available after timer"}
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"} // Dynamic type based on state
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                validatePasswords(e.target.value, confirmPassword);
              }}
              sx={{ mb: 2, mt: 3 }}
              InputProps={{ // Added InputProps for the eye icon
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowNewPassword}
                      onMouseDown={handleMouseDownNewPassword}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"} // Dynamic type based on state
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                validatePasswords(newPassword, e.target.value);
              }}
              sx={{ mb: 2 }}
              error={Boolean(passwordError)}
              helperText={passwordError}
              InputProps={{ // Added InputProps for the eye icon
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <MDButton
              variant="contained"
              color="info" // Changed to "info" for a common blue, or use sx for custom
              fullWidth
              onClick={handleResetPassword}
              disabled={loading || !newPassword || !confirmPassword || Boolean(passwordError)}
              sx={{ mt: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </MDButton>
          </>
        )}

        <Button
          variant="text"
          fullWidth
          sx={{ mt: 2, color: "grey.600", textTransform: "none" }}
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>
      <ToastContainer
        position="bottom-right"
        style={{ width: "100%", maxWidth: "none" }}
      />
    </Box>
  );
};

export default ForgotPassword;